import { LogoIcon } from '@/components/icons/footer/LogoIcon';
import { useKioskNavigation } from '@/hooks/useKioskNavigation';
import { useOrientation } from '@/hooks/useOrientation';
import { useConfigStore } from '@/stores';
import type { GNBItemType } from '@/types';
import { getLogoUrl } from '@/utils/logo';
import { clsx } from 'clsx';
import { memo, useMemo } from 'react';
import { GNBItem } from './GNBItem';
import styles from './GNBLayout.module.scss';

interface GNBLayoutProps {
    gnbItems: GNBItemType[];
}

export const GNBLayout = memo(({ gnbItems }: GNBLayoutProps) => {
    const { state } = useKioskNavigation();
    const { isHorizontal } = useOrientation();
    const screenMode = useConfigStore((state) => state.config?.ui?.screenMode);
    const buildingType = useConfigStore((state) => state.config?.server?.building?.buildingType);
    const contrast = useConfigStore((state) => state.config.ui.contrast);

    const containerClassName = useMemo(
        () =>
            clsx(
                styles['gnb-menu'],
                isHorizontal ? styles['gnb-menu--horizontal'] : styles['gnb-menu--vertical'],
                screenMode === 'fullscreen' ? styles['gnb-menu--fullscreen'] : styles['gnb-menu--half-screen']
            ),
        [isHorizontal, screenMode]
    );

    const LogoSection = useMemo(() => {
        if (!isHorizontal || screenMode === 'fullscreen') return null;
        const logoUrl = getLogoUrl(isHorizontal, buildingType, contrast, screenMode);
        return (
            <div className={styles['gnb-menu__navigation__wrapper']}>
                <div className={styles['gnb-menu__navigation__wrapper__logo']}>{logoUrl ? <img src={logoUrl} alt="Logo" /> : <LogoIcon />}</div>
            </div>
        );
    }, [isHorizontal, buildingType, contrast, screenMode]);

    return (
        <div className={containerClassName}>
            <nav className={styles['gnb-menu__navigation']} role="navigation" aria-label="주요 메뉴">
                {LogoSection}
                {gnbItems.map((item, index) => (
                    <GNBItem
                        key={item.id}
                        item={item}
                        index={index}
                        isActive={state.activeElements?.gnb === item.id}
                        isFocused={state.currentSection === 'gnb' && state.focusedIndex === index}
                    />
                ))}
            </nav>
        </div>
    );
});

GNBLayout.displayName = 'GNBLayout';
